import proj4 from "proj4";
import type { TextKey } from "../../i18n/en.ts";
import { WGS84_PROJ4 } from "../geo/crs.ts";
import { findEpsgByCode } from "../geo/epsgSearch.ts";
import { parseDbf } from "./dbfParser.ts";
import { parseShp } from "./shpParser.ts";

export type GeojsonGeometryType = "Point" | "LineString" | "Polygon";

export interface RawGeometry {
  type: string;
  coordinates: unknown;
}

export interface RawFeature {
  type: "Feature";
  properties?: Record<string, unknown> | null;
  geometry: RawGeometry | null;
}

export interface RawFeatureCollection {
  type: "FeatureCollection";
  features: RawFeature[];
}

export interface DetectedLayer {
  fileName: string;
  featureCount: number;
  /** `null` if empty or mixes geometry types — a mixed layer can't be assigned a SWMM element type until it's split. */
  geometryType: GeojsonGeometryType | null;
  propertyNames: string[];
  featureCollection: RawFeatureCollection;
  /** `null` ONLY for a Shapefile set whose `.prj` is missing or unparseable — paired with `blockedReason`. */
  sourceCrs: string | null;
  /** A translatable `TextKey`, unlike `LayerDetectionError.message` below, which stays raw untranslated text. */
  blockedReason: TextKey | null;
}

export interface LayerDetectionError {
  fileName: string;
  message: string;
}

interface LayerDetectionResult {
  layers: DetectedLayer[];
  errors: LayerDetectionError[];
}

function isGeojsonGeometryType(type: string): type is GeojsonGeometryType {
  return type === "Point" || type === "LineString" || type === "Polygon";
}

/** GeoJSON's legacy top-level `crs` member — deprecated by RFC7946 (which mandates WGS84) but still emitted by older GDAL/ogr2ogr exports. Any absent/unrecognized form falls back to WGS84. */
async function resolveGeojsonCrs(fc: { crs?: unknown }): Promise<string> {
  const member = fc.crs as { type?: string; properties?: { name?: string } } | string | undefined;
  const name = typeof member === "string" ? member : member?.properties?.name;
  if (!name) return WGS84_PROJ4;
  if (/CRS84$/i.test(name)) return WGS84_PROJ4;
  const match = /EPSG:{1,2}(\d+)/i.exec(name);
  if (!match) return WGS84_PROJ4;
  const entry = await findEpsgByCode(match[1]);
  return entry?.proj4 ?? WGS84_PROJ4;
}

async function detectGeojsonLayer(fileName: string, parsed: unknown): Promise<DetectedLayer> {
  const fc = parsed as Partial<RawFeatureCollection & { crs?: unknown }> | null;
  if (!fc || fc.type !== "FeatureCollection" || !Array.isArray(fc.features)) {
    throw new Error("Not a GeoJSON FeatureCollection");
  }

  const propertyNames: string[] = [];
  const seenProps = new Set<string>();
  // undefined = no feature seen yet, null = features disagree on geometry.
  let geometryType: GeojsonGeometryType | null | undefined;

  for (const feature of fc.features) {
    const gType = feature.geometry?.type;
    if (gType) {
      // An unsupported type (MultiPoint/MultiLineString/MultiPolygon/
      // GeometryCollection) mixed into an otherwise-uniform layer used to
      // be silently ignored HERE — the layer still came out classified as
      // whatever plain Point/LineString/Polygon type its OTHER features
      // shared, and `importLayer`'s own per-feature code (which assumes a
      // flat LineString/ring coordinate shape) would then misread that
      // Multi*'s own one-level-deeper nesting as raw [x,y] pairs, writing
      // garbage coordinate rows. Blocked the same way a genuine
      // Point/LineString/Polygon mismatch already is, instead.
      geometryType =
        isGeojsonGeometryType(gType) && (geometryType === undefined || geometryType === gType)
          ? gType
          : null;
    }
    for (const key of Object.keys(feature.properties ?? {})) {
      if (!seenProps.has(key)) {
        seenProps.add(key);
        propertyNames.push(key);
      }
    }
  }

  return {
    fileName,
    featureCount: fc.features.length,
    geometryType: geometryType ?? null,
    propertyNames,
    featureCollection: fc as RawFeatureCollection,
    sourceCrs: await resolveGeojsonCrs(fc),
    blockedReason: null,
  };
}

const SHAPEFILE_EXTENSIONS = ["shp", "dbf", "shx", "prj"] as const;
type ShapefileExtension = (typeof SHAPEFILE_EXTENSIONS)[number];

interface ShapefileFileSet {
  shp?: File;
  dbf?: File;
  shx?: File;
  prj?: File;
}

function shapefileExtension(fileName: string): ShapefileExtension | null {
  const match = /\.(shp|dbf|shx|prj)$/i.exec(fileName);
  return match ? (match[1].toLowerCase() as ShapefileExtension) : null;
}

/** Safe only because `shapefileExtension` already confirmed a known 3-letter extension is there. */
function stripShapefileExtension(fileName: string): string {
  return fileName.slice(0, -4);
}

function groupShapefileSets(files: readonly File[]): Map<string, ShapefileFileSet> {
  const sets = new Map<string, ShapefileFileSet>();
  for (const file of files) {
    const ext = shapefileExtension(file.name);
    if (!ext) continue;
    const key = stripShapefileExtension(file.name).toLowerCase();
    const set = sets.get(key) ?? {};
    set[ext] = file;
    sets.set(key, set);
  }
  return sets;
}

async function detectShapefileLayer(
  fileName: string,
  set: { shp: File; dbf: File; prj?: File }
): Promise<DetectedLayer> {
  const shp = parseShp(await set.shp.arrayBuffer());
  const dbf = parseDbf(await set.dbf.arrayBuffer());
  if (shp.records.length !== dbf.rows.length) {
    throw new Error(
      `Shapefile record count (${shp.records.length}) doesn't match .dbf row count (${dbf.rows.length})`
    );
  }

  const features: RawFeature[] = [];
  // undefined = no feature seen yet, null = features disagree on geometry.
  let geometryType: GeojsonGeometryType | null | undefined;

  for (let i = 0; i < shp.records.length; i++) {
    const record = shp.records[i];
    const properties = dbf.rows[i];
    if (record.parts.length === 0) continue; // a real, if empty, Null-shape record

    const geometries: { type: GeojsonGeometryType; coordinates: unknown }[] =
      shp.shapeType === 1
        ? [{ type: "Point", coordinates: record.parts[0][0] }]
        : shp.shapeType === 3
          ? record.parts.map((part) => ({ type: "LineString" as const, coordinates: part }))
          : [{ type: "Polygon", coordinates: record.parts }];

    for (const geometry of geometries) {
      geometryType =
        geometryType === undefined || geometryType === geometry.type ? geometry.type : null;
      features.push({ type: "Feature", properties, geometry });
    }
  }

  let sourceCrs: string | null = null;
  let blockedReason: TextKey | null = "gisImport.missingPrj";
  if (set.prj) {
    const text = (await set.prj.text()).trim();
    try {
      proj4(text); // validates parseability only — the WKT text itself is what gets stored/reused
      sourceCrs = text;
      blockedReason = null;
    } catch {
      blockedReason = "gisImport.prjUnparseable";
    }
  }

  return {
    fileName,
    featureCount: features.length,
    geometryType: geometryType ?? null,
    propertyNames: dbf.fields.map((f) => f.name),
    featureCollection: { type: "FeatureCollection", features },
    sourceCrs,
    blockedReason,
  };
}

/** A bad file (invalid JSON, missing shapefile companion) is reported in `errors` rather than aborting the whole folder's import. */
export async function detectLayersFromFiles(files: readonly File[]): Promise<LayerDetectionResult> {
  const layers: DetectedLayer[] = [];
  const errors: LayerDetectionError[] = [];

  for (const file of files) {
    if (!/\.(geo)?json$/i.test(file.name)) continue;
    try {
      const parsed: unknown = JSON.parse(await file.text());
      layers.push(await detectGeojsonLayer(file.name, parsed));
    } catch (err) {
      errors.push({
        fileName: file.name,
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  for (const set of groupShapefileSets(files).values()) {
    if (!set.shp && !set.dbf && !set.shx) continue; // a lone .prj with no real shapefile companion — not a set attempt at all
    const anyFile = set.shp ?? set.dbf ?? set.shx ?? set.prj!;
    const realBaseName = stripShapefileExtension(anyFile.name);
    const fileName = `${realBaseName}.shp`;

    const missing = SHAPEFILE_EXTENSIONS.filter(
      (ext): ext is "shp" | "dbf" | "shx" => ext !== "prj" && !set[ext]
    );
    if (missing.length > 0) {
      errors.push({
        fileName,
        message: `Missing required file(s): ${missing.map((ext) => `${realBaseName}.${ext}`).join(", ")}`,
      });
      continue;
    }
    try {
      layers.push(
        await detectShapefileLayer(fileName, { shp: set.shp!, dbf: set.dbf!, prj: set.prj })
      );
    } catch (err) {
      errors.push({ fileName, message: err instanceof Error ? err.message : String(err) });
    }
  }

  return { layers, errors };
}
